import React, {createContext, useContext, useState, useEffect} from 'react';
import axios from 'axios';

const PhaseContext = createContext();

export const usePhase = () => {
    const context = useContext(PhaseContext);
    if (!context) {
        throw new Error('usePhase must be used within a PhaseProvider');
    }
    return context;
};

export const PhaseProvider = ({children}) => {
    const [phase, setPhase] = useState({
        currentPhase: 'Closed',
        name: 'Loading...',
        description: '',
        uploadingStartsAt: null,
        votingStartsAt: null,
        closedAt: null,
        autoTransition: false,
        nextTransition: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCurrentPhase = async () => {
        try {
            const response = await axios.get('/api/phases/current');
            setPhase(response.data);
            setError(null);
        } catch (error) {
            console.error('Error fetching current phase:', error);
            setError(error.response?.data?.error || 'Failed to fetch phase information');
            // Set default closed phase on error
            setPhase({
                currentPhase: 'Closed',
                name: 'System Offline',
                description: 'Unable to fetch phase information',
                uploadingStartsAt: null,
                votingStartsAt: null,
                closedAt: null,
                autoTransition: false,
                nextTransition: null,
            });
        } finally {
            setLoading(false);
        }
    };

    const checkActionAllowed = async (action) => {
        try {
            const response = await axios.get(`/api/phases/check/${action}`);
            return response.data.allowed;
        } catch (error) {
            console.error('Error checking action permission:', error);
            return false;
        }
    };

    const isPhaseAllowed = (action) => {
        switch (action) {
            case 'upload':
            case 'edit-art':
            case 'delete-art':
                return phase.currentPhase === 'Uploading';
            case 'vote':
                return phase.currentPhase === 'Voting';
            case 'view-art':
                return true; // Always allowed
            default:
                return false;
        }
    };

    const getPhaseDisplay = () => {
        const phaseColors = {
            Uploading: 'bg-green-100 text-green-800',
            Voting: 'bg-blue-100 text-blue-800',
            Closed: 'bg-gray-100 text-gray-800',
        };

        const phaseIcons = {
            Uploading: '📤',
            Voting: '🗳️',
            Closed: '🔒',
        };

        return {
            color: phaseColors[phase.currentPhase] || 'bg-gray-100 text-gray-800',
            icon: phaseIcons[phase.currentPhase] || '❓',
            name: phase.currentPhase,
        };
    };

    const formatTimeUntilTransition = () => {
        if (!phase.nextTransition) return null;

        const timeUntil = phase.nextTransition.timeUntil;
        const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
            return `${days}d ${hours}h`;
        } else if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else {
            return `${minutes}m`;
        }
    };

    useEffect(() => {
        fetchCurrentPhase();

        // Set up polling to check for phase changes every minute
        const interval = setInterval(fetchCurrentPhase, 60000);

        return () => clearInterval(interval);
    }, []);

    const contextValue = {
        phase,
        loading,
        error,
        fetchCurrentPhase,
        checkActionAllowed,
        isPhaseAllowed,
        getPhaseDisplay,
        formatTimeUntilTransition,
    };

    return <PhaseContext.Provider value={contextValue}>{children}</PhaseContext.Provider>;
};
